import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useBrands } from '../contexts/BrandContext'
import { VcardEditorView } from '../components/VcardEditorView'
import { loadMyVcard, saveMyVcard } from '../services/vcardStore'
import { notifySuccess, notifyError } from '../utils/notify'
import { savedToast } from '../utils/vcardSave'
import type { Vcard } from '../types'

type LoadStatus = 'loading' | 'ready' | 'notfound'

/** „Meine vCard" — Editor der eigenen vCard des Users (Spec §5.7, /me/vcard). */
export function MyVcardPage() {
  const { session } = useAuth()
  const { resolveBrand } = useBrands()
  const [vcard, setVcard] = useState<Vcard | null>(null)
  const [status, setStatus] = useState<LoadStatus>('loading')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!session) return
    let cancelled = false
    setStatus('loading')
    loadMyVcard()
      .then((vc) => {
        if (cancelled) return
        setVcard(vc)
        setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) setStatus('notfound')
      })
    return () => {
      cancelled = true
    }
  }, [session])

  const handleSave = useCallback(async (payload: Parameters<typeof saveMyVcard>[0], cleared: string[]) => {
    setSaving(true)
    try {
      await saveMyVcard(payload)
      notifySuccess(savedToast(cleared.length))
    } catch {
      notifyError('Speichern fehlgeschlagen, bitte erneut.')
    } finally {
      setSaving(false)
    }
  }, [])

  if (status === 'loading')
    return (
      <section className="panel">
        <div className="head">
          <h2>Meine vCard</h2>
        </div>
        <div className="empty">vCard wird geladen …</div>
      </section>
    )
  if (status === 'notfound' || !vcard)
    return (
      <section className="panel">
        <div className="head">
          <h2>Meine vCard</h2>
        </div>
        <div className="empty">Keine vCard mit deinem Konto verknüpft.</div>
      </section>
    )

  return (
    <VcardEditorView
      vcard={vcard}
      brand={resolveBrand(vcard.brandKey)}
      scopeHint="Nur deine eigene vCard."
      showBack={false}
      saving={saving}
      onSave={handleSave}
    />
  )
}
